import { get, post, put } from "aws-amplify/api";
import { default as React, useState, useEffect, useCallback, useMemo } from "react";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import { Alert, Button, Col, Row, Form } from "react-bootstrap";
import { PacmanLoader } from "react-spinners";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import "containers/common/List.css";
import "containers/common/Table.css";
import { PAGE_SIZES } from "utils/pageSizeConfig";

import {
  createColumnHelper,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

function AuSuburbList({
  isAuthenticated,
  user,
}) {
  const auSuburbIdPrefix = "au-suburb-list";

  const [isLoading, setIsLoading] = useState(true);
  const [ui, setUi] = useState({ showAlert: false, errorMessage: "" });
  const [sorting, setSorting] = useState([]);
  const [data, setData] = useState([]);
  const [suburb, setSuburb] = useState("");
  const [scope, setScope] = useState("");
  const [cityscopeOptions, setCityscopeOptions] = useState([]);

  // --- API Functions ---
  const getData = useCallback(async () => {
    const response = await get({
      apiName: "colin",
      path: "/ausuburbs",
    }).response;
    try {
      const cs = await response.body.json();
      return cs;
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    }
  }, []);

  const getCityscopes = useCallback(async () => {
    try {
      const response = await get({
        apiName: "colin",
        path: "/cityscopes/dropdown"
      }).response;
      const options = await response.body.json();

      if (options && Array.isArray(options)) {
        const formatted = options.map(opt => ({
          label: opt.label || opt.cityscope_name || opt,
          value: opt.value || opt.cityscope_name || opt
        }));
        setCityscopeOptions(options);
        console.log("Fetched Options:", formatted);
      }
    } catch (err) {
      console.error("Error getting cityscopes:", err);
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    if (!isAuthenticated) {
      return;
    }

    (async () => {
      try {
        const localData = await getData();
        setData(localData || []);
        getCityscopes();
      } catch (e) {
        alert(e);
        setUi({ showAlert: true, errorMessage: "Failed to load data." });
      }
      setIsLoading(false);
    })();
  }, [isAuthenticated, getData, getCityscopes]);

  const saveRecord = async (record) => {
    const isNew = record.new_record;
    const path = isNew ? "/ausuburbs/new" : `/ausuburbs/${record["postconv_id"]}`;
    const method = isNew ? post : put;

    const result = await method({
      apiName: "colin",
      path: path,
      options: { body: { ...record, last_edited_by: user } }
    });

    const response = await result.response;
    const body = await response.body.json();
    return body;
  };

  // --- Table Logic ---
  const updateTableState = (rowIndex, columnId, value) => {
    setData(old => old.map((record, index) => (
      index === rowIndex
        ? { ...record, [columnId]: value, modified: true }
        : record
    )));
  };

  // --- Save Logic Functions ---
  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      const response = await get({
        apiName: "colin",
        path: "/ausuburbs",
        options: {
          queryParams: {
            suburb: suburb,
            scope: scope
          }
        }
      }).response;

      const searchResults = await response.body.json();
      setData(searchResults || []);
    } catch (err) {
      console.error("Search error:", err);
      setUi({ showAlert: true, errorMessage: "Search failed. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  const validateRecord = (record) => {
    if (!record.suburb || record.suburb.trim() === "") {
      return [false, "Suburb cannot be blank. The record has not been saved"];
    }
    if (record.suburb.trim().length > 100) {
      return [false, `Suburb cannot exceed 100 characters. "${record.suburb.trim()}" is not valid. The record has not been saved`];
    }
    if (!record.state || record.state.trim() === "") {
      return [false, "State cannot be blank. The record has not been saved"];
    }
    if (record.state.trim().length > 10) {
      return [false, `State cannot exceed 10 characters. "${record.state.trim()}" is not valid. The record has not been saved`];
    }
    if (!record.postcode || String(record.postcode).trim() === "") {
      return [false, "Postcode cannot be blank. The record has not been saved"];
    }
    if (String(record.postcode).trim().length > 4) {
      return [false, "Postcode cannot exceed 4 characters. The record has not been saved"];
    }
    if (!Number.isInteger(Number(String(record.postcode).trim())) || isNaN(Number(String(record.postcode).trim()))) {
      return [false, `Postcode must be a whole number (e.g. 3000). "${String(record.postcode).trim()}" is not valid. The record has not been saved`];
    }
    return [true, ""];
  };

  const getNewRecordValidationMessage = (record, activeFieldId) => {
    const requiredFieldIds = ["suburb", "state", "postcode"];
    if (!requiredFieldIds.includes(activeFieldId)) {
      return "";
    }

    const suburb = String(record.suburb ?? "").trim();
    const state = String(record.state ?? "").trim();
    const postcode = String(record.postcode ?? "").trim();

    if (activeFieldId === "suburb" && suburb === "") {
      return "Suburb cannot be blank. The record has not been saved";
    }
    if (activeFieldId === "state" && state === "") {
      return "State cannot be blank. The record has not been saved";
    }
    if (activeFieldId === "postcode" && postcode === "") {
      return "Postcode cannot be blank. The record has not been saved";
    }

    if (suburb !== "" && suburb.length > 100) {
      return `Suburb cannot exceed 100 characters. "${suburb}" is not valid. The record has not been saved`;
    }

    if (state !== "" && state.length > 10) {
      return `State cannot exceed 10 characters. "${state}" is not valid. The record has not been saved`;
    }

    if (postcode !== "") {
      if (postcode.length > 4) {
        return "Postcode cannot exceed 4 characters. The record has not been saved";
      }
      if (!/^\d+$/.test(postcode)) {
        return `Postcode must be a whole number (e.g. 3000). "${postcode}" is not valid. The record has not been saved`;
      }
    }

    return "";
  };

  const handleSave = async ({ row, column, value }) => {
    const rowIndex = row.index;
    const normalizedValue = column.id === "postcode"
      ? String(value ?? "").trim().slice(0, 4)
      : column.id === "suburb"
        ? String(value ?? "").trim().slice(0, 100)
        : value;
    const recordToSave = { ...row.original, [column.id]: normalizedValue };
    const [isValid, message] = validateRecord(recordToSave);

    setUi(prev => ({ ...prev, showAlert: false, errorMessage: "" }));

    // For new rows, allow users to progressively fill required fields.
    // Do not persist until suburb/state/postcode are all valid.
    if (recordToSave.new_record) {
      const fieldMessage = getNewRecordValidationMessage(recordToSave, column.id);
      if (fieldMessage) {
        setUi({ showAlert: true, errorMessage: fieldMessage });
        return true;
      }

      if (!isValid) {
        // Keep partial new-row edits without cross-field alerts.
        return true;
      }
    }

    if (!isValid) {
      setUi({ showAlert: true, errorMessage: message });
      return true;
    }

    setData(old => old.map((r, idx) =>
      idx === rowIndex ? { ...r, isSaving: true, [column.id]: normalizedValue } : r
    ));

    try {
      const response = await saveRecord(recordToSave);

      if (response && response.result === false) {
        setUi({ showAlert: true, errorMessage: response.error_message });
        setData(old => old.map((r, idx) => idx === rowIndex ? { ...r, isSaving: false } : r));
        return false;
      }

      const persistedId = recordToSave.new_record
        ? (response?.id || response?.postconv_id || row.original.postconv_id)
        : row.original.postconv_id;

      setData(old => old.map((r, idx) =>
        idx === rowIndex
          ? {
            ...r,
            [column.id]: normalizedValue,
            postconv_id: persistedId,
            isSaving: false,
            new_record: false,
            modified: false
          }
          : r
      ));
      return true;
    } catch (err) {
      console.error("Save failed", err);
      setData(old => old.map((r, idx) => idx === rowIndex ? { ...r, isSaving: false } : r));
      return false;
    }
  };

  const addRecord = () => {
    const newEntry = {
      postconv_id: "",
      cityscope: "",
      suburb: "",
      state: "",
      postcode: "",
      created_by: user,
      last_edited_by: user,
      new_record: true,
      isSaving: false
    };
    setData(prev => [newEntry, ...prev]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "suburb") setSuburb(value);
    else if (name === "scope") setScope(value);
  };

  const handleCityscopeChange = (selectedOptions, { row, column }) => {
    const newValue = selectedOptions.map(option => option.value).join(';');
    updateData(row.index, column.id, newValue);
  };

  const handleCityscopeBlur = ({ row, column }) => {
    handleSave({ row: row, column: column });
  };

  const updateData = (rowIndex, columnId, value) => {
    setData((old) =>
      old.map((row, index) => {
        if (index === rowIndex) {

          let finalValue = value;

          if (columnId === 'postcode') {
            finalValue = String(value).trim();
          } else {
            if (typeof old[rowIndex][columnId] === 'number') {
              finalValue = Number(value);
            }
          }
          console.log("UPDATE")
          return {
            ...old[rowIndex],
            [columnId]: finalValue,
            modified: true,
          };
        }
        return row;
      })
    );
  };

  // --- Column Definitions ---
  const columns = useMemo(() => [
    columnHelper.accessor("postconv_id", {
      id: "postconv_id",
      header: "ID",
      cell: (info) => {
        const { isSaving, new_record } = info.row.original;
        if (isSaving) return <span className="text-primary">Saving...</span>;
        if (new_record) return <span className="badge badge-info">New</span>;
        return info.getValue() || '';
      },
      size: 50,
    }),
    columnHelper.accessor("cityscope", {
      id: "cityscope",
      header: "Cityscope",
      cell: (props) => {
        return (
          <EditableCell
            {...props}
            isMulti={true}
            options={cityscopeOptions || []}
          />
        );
      },
      size: 100,
    }),
    columnHelper.accessor("suburb", {
      id: "suburb",
      header: "Suburb",
      cell: EditableCell,
      meta: { maxLength: 100 },
      size: 50,
    }),
    columnHelper.accessor("state", {
      id: "state",
      header: "State",
      cell: EditableCell,
      meta: { maxLength: 10 },
      size: 50,
    }),
    columnHelper.accessor("postcode", {
      id: "postcode",
      header: "Postcode",
      cell: EditableCell,
      meta: { maxLength: 4 },
      size: 50,
    }),
    columnHelper.accessor("created_by", {
      id: "created_by",
      header: "Created By",
      cell: (info) => info.getValue(),
      size: 50,
    }),
    columnHelper.accessor("last_edited_by", {
      id: "last_edited_by",
      header: "Last Edited By",
      cell: (info) => info.getValue(),
      size: 50,
    }),
  ], [cityscopeOptions]);

  const createId = useMemo(
    () => createElementIdFactory(auSuburbIdPrefix),
    [auSuburbIdPrefix]
  );

  const tableElementIds = useMemo(
    () => createReusableTableElementIds(createId),
    [createId]
  );

  if (!isAuthenticated) return null;

  // --- Render Functions ---
  return (
    <div className="mt-3">
      <Alert show={ui.showAlert} variant="danger" onClose={() => setUi({ ...ui, showAlert: false })} dismissible>
        {ui.errorMessage}
      </Alert>

      <Form id={createId("search", "form")} onSubmit={handleSubmit}>
        <Row>
          <Col lg={2}>
            <Form.Group controlId="suburb">
              <Form.Control id={createId("suburb", "input")} name="suburb" value={suburb} onChange={handleChange} type="input" placeholder="Suburb" />
            </Form.Group>
          </Col>

          <Col lg={2}>
            <Form.Group controlId="scope">
              <Form.Control id={createId("scope", "input")} name="scope" type="input" value={scope} onChange={handleChange} placeholder="Cityscope" />
            </Form.Group>
          </Col>
          <Col lg={2}>
            <Button id={createId("search", "button")} type="submit" className="float-left">Search</Button>
          </Col>
        </Row>
      </Form>

      <Col className="text-right" style={{ marginBottom: '12px' }}>
        <Button id={createId("add", "button")} variant="secondary" size="sm" onClick={addRecord}>Add</Button>
      </Col>

      <Row>
        <Col>
          {isLoading ? (
            <div className="outer-div">
              <div className="sweet-loading inner-div">
                <PacmanLoader
                  cssOverride={{ display: "block", margin: "0 auto" }}
                  size={20}
                  color={"#e03c31"}
                  loading={isLoading}
                />
              </div>
            </div>
          ) : (
            <div className="custom-data-table">
              <GenericTable
                columns={columns}
                data={data}
                section="au-suburb-list"
                elementIds={tableElementIds}
                pageSize={PAGE_SIZES.auSuburb}
                enablePagination={true}
                state={{ sorting }}
                onSortingChange={setSorting}
                enableSortingRemoval={false}
                meta={{
                  updateData: updateTableState,
                  handleSave: handleSave,
                  shouldForceSaveOnBlur: ({ row, column, value }) => (
                    !!row?.original?.new_record
                    && ["suburb", "state", "postcode"].includes(column?.id)
                    && String(value ?? "").trim() === ""
                  ),
                  handleCityscopeChange: handleCityscopeChange,
                  handleCityscopeBlur: handleCityscopeBlur,
                  cityscopeOptions: cityscopeOptions,
                }}
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
}

export default AuSuburbList;